import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  getVehicles,
  getDrivers,
  getLocations,
  getAlerts,
  setAuthToken,
  setElevatedToken,
  setUnauthorizedHandler,
  getMe,
  login as apiLogin,
} from '../services/api';

const AppContext = createContext();

const STORAGE_KEY = 'fleet.auth';
const ELEVATED_STORAGE_KEY = 'fleet.auth.elevated';

const readStoredAuth = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.token) return null;
    return parsed;
  } catch (_err) {
    return null;
  }
};

const writeStoredAuth = (key, auth) => {
  try {
    if (auth && auth.token) {
      localStorage.setItem(key, JSON.stringify(auth));
    } else {
      localStorage.removeItem(key);
    }
  } catch (_err) {
    // storage errors are non-fatal
  }
};

function isManagementRole(role) {
  return role === 'admin' || role === 'dispatcher';
}

export const AppProvider = ({ children }) => {
  const [vehicles,  setVehicles]  = useState([]);
  const [drivers,   setDrivers]   = useState([]);
  const [locations, setLocations] = useState([]);
  const [alerts,    setAlerts]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [authReady, setAuthReady] = useState(false);

  const [admin, setAdmin] = useState(() => {
    const stored = readStoredAuth(STORAGE_KEY);
    if (stored) {
      setAuthToken(stored.token);
      return stored.admin || null;
    }
    return null;
  });

  const [elevatedAdmin, setElevatedAdmin] = useState(() => {
    const stored = readStoredAuth(ELEVATED_STORAGE_KEY);
    if (stored) {
      setElevatedToken(stored.token);
      return stored.admin || null;
    }
    return null;
  });

  const clearElevated = useCallback(() => {
    setElevatedToken(null);
    writeStoredAuth(ELEVATED_STORAGE_KEY, null);
    setElevatedAdmin(null);
  }, []);

  const logout = useCallback(() => {
    setAuthToken(null);
    setElevatedToken(null);
    writeStoredAuth(STORAGE_KEY, null);
    writeStoredAuth(ELEVATED_STORAGE_KEY, null);
    setAdmin(null);
    setElevatedAdmin(null);
    setVehicles([]);
    setDrivers([]);
    setLocations([]);
    setAlerts([]);
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password, portal = 'app', options = {}) => {
    const res = await apiLogin(email, password, portal);
    const profile = res.admin;
    const auth = { token: res.token, admin: profile, portal: res.portal || portal };

    if (options.elevate && readStoredAuth(STORAGE_KEY)?.token) {
      if (!isManagementRole(profile?.role)) {
        throw new Error('Use an admin or dispatcher account to unlock assignments.');
      }
      setElevatedToken(auth.token);
      writeStoredAuth(ELEVATED_STORAGE_KEY, auth);
      setElevatedAdmin(profile);
      return profile;
    }

    setAuthToken(auth.token);
    writeStoredAuth(STORAGE_KEY, auth);
    setAdmin(profile);

    if (isManagementRole(profile?.role)) {
      setElevatedToken(auth.token);
      writeStoredAuth(ELEVATED_STORAGE_KEY, auth);
      setElevatedAdmin(profile);
    } else {
      clearElevated();
    }

    return profile;
  }, [clearElevated]);

  useEffect(() => {
    setUnauthorizedHandler((scope) => {
      if (scope === 'elevated') {
        clearElevated();
        return;
      }
      logout();
    });
    return () => setUnauthorizedHandler(null);
  }, [logout, clearElevated]);

  useEffect(() => {
    const stored = readStoredAuth(STORAGE_KEY);
    if (!stored?.token) {
      setAuthReady(true);
      return;
    }

    setAuthToken(stored.token);

    const tasks = [
      getMe(stored.token)
        .then((data) => {
          const profile = data?.admin || stored.admin || null;
          if (profile) {
            setAdmin(profile);
            writeStoredAuth(STORAGE_KEY, { token: stored.token, admin: profile });
          } else {
            logout();
          }
        })
        .catch(() => logout()),
    ];

    const elevatedStored = readStoredAuth(ELEVATED_STORAGE_KEY);
    if (elevatedStored?.token) {
      setElevatedToken(elevatedStored.token);
      tasks.push(
        getMe(elevatedStored.token)
          .then((data) => {
            const profile = data?.admin || elevatedStored.admin || null;
            if (profile && isManagementRole(profile.role)) {
              setElevatedAdmin(profile);
              writeStoredAuth(ELEVATED_STORAGE_KEY, { token: elevatedStored.token, admin: profile });
            } else {
              clearElevated();
            }
          })
          .catch(() => clearElevated())
      );
    }

    Promise.all(tasks).finally(() => setAuthReady(true));
  }, [logout, clearElevated]);

  const refresh = useCallback(async () => {
    try {
      const [v, d, l, a] = await Promise.all([
        getVehicles(),
        getDrivers(),
        getLocations(),
        getAlerts(),
      ]);
      setVehicles(Array.isArray(v) ? v : []);
      setDrivers(Array.isArray(d) ? d : []);
      setLocations(Array.isArray(l) ? l : []);
      setAlerts(Array.isArray(a) ? a : []);
    } catch (err) {
      console.error(err);
      setVehicles([]);
      setDrivers([]);
      setLocations([]);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const userRole = admin?.role || null;
  const elevatedRole = elevatedAdmin?.role || null;
  const isAuthenticated = Boolean(admin && userRole);
  const isAdmin = userRole === 'admin' || elevatedRole === 'admin';
  const isDispatcher = userRole === 'dispatcher' || elevatedRole === 'dispatcher';
  const isStaff = userRole === 'staff' && !isManagementRole(elevatedRole);
  const canManageAssignments = isAdmin || isDispatcher;
  const role = isAdmin ? 'admin' : (isDispatcher ? 'dispatcher' : (isStaff ? 'staff' : 'driver'));

  useEffect(() => {
    if (!authReady) return;
    if (isAuthenticated) {
      setLoading(true);
      refresh();
      return;
    }
    setVehicles([]);
    setDrivers([]);
    setLocations([]);
    setAlerts([]);
    setLoading(false);
  }, [authReady, isAuthenticated, refresh]);

  return (
    <AppContext.Provider value={{
      vehicles, drivers, locations, alerts, loading, refresh,
      admin, elevatedAdmin, isAdmin, isDispatcher, isStaff,
      isAuthenticated, canManageAssignments, role,
      authReady, login, logout, clearElevated,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
